import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

const ROLE_INFO = {
  admin:        { label: 'Admin',        badgeClass: 'badge-pur', cardAccent: 'pur', desc: 'Full access — edit, save, manage users' },
  data_updater: { label: 'Data Updater', badgeClass: 'badge-blu', cardAccent: 'blu', desc: 'Can edit data and save runs' },
  viewer:       { label: 'Viewer',       badgeClass: 'badge-grn', cardAccent: 'grn', desc: 'Read-only — view dashboards and history' },
};

const ALLOWED_DOMAINS = ['@kunlene-sz.com', '@kunlene-yn.com'];
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

export default function Users({ addToast }) {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      setUsers(data || []);
    } catch (e) {
      addToast('Could not load users: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  if (!isAdmin) {
    return (
      <div className="page-container">
        <div className="page-hdr">
          <h2>Access denied</h2>
          <p>Only admins can manage users.</p>
        </div>
      </div>
    );
  }

  async function handleInvite(e) {
    e.preventDefault();
    const email = inviteEmail.toLowerCase().trim();

    if (!ALLOWED_DOMAINS.some(d => email.endsWith(d))) {
      addToast('Only @kunlene-sz.com or @kunlene-yn.com addresses allowed', 'error');
      return;
    }
    if (users.some(u => u.email === email)) {
      addToast('User already exists in your team roster', 'error');
      return;
    }

    setInviting(true);
    try {
      // Get current session token to authenticate the request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        addToast('Session expired — please sign in again', 'error');
        setInviting(false);
        return;
      }

      const res = await fetch('/api/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email,
          role: inviteRole,
          inviterEmail: currentUser.email,
          supabaseUrl: SUPABASE_URL,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        addToast(data.error || 'Invite failed', 'error');
        setInviting(false);
        return;
      }

      addToast(
        `✉️ Invitation sent to ${email}. They'll receive an email with a setup link.`,
        'success'
      );
      setInviteEmail('');
      setInviteRole('viewer');
      loadUsers();
    } catch (e) {
      addToast('Invite failed: ' + e.message, 'error');
    } finally {
      setInviting(false);
    }
  }

  async function changeRole(profileId, newRole) {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('id', profileId);
      if (error) throw error;
      addToast('Role updated', 'success');
      loadUsers();
    } catch (e) {
      addToast('Update failed: ' + e.message, 'error');
    }
  }

  async function removeUser(userId, email) {
    if (email === currentUser.email) {
      addToast('Cannot remove yourself', 'error');
      return;
    }
    if (!window.confirm(`Remove access for ${email}?\n\nThey will be signed out, their auth account will be deleted, and they will be unable to log in. This cannot be undone.`)) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId,
          email,
          supabaseUrl: SUPABASE_URL,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Remove failed');
      addToast(`Removed ${email}`, 'success');
      loadUsers();
    } catch (e) {
      addToast('Remove failed: ' + e.message, 'error');
    }
  }

  async function resendInvite(email) {
    if (!window.confirm(`Resend invitation email to ${email}?`)) return;
    try {
      await supabase.auth.getSession();
      // Use Supabase resetPasswordForEmail as a "resend invite" — same effect
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      addToast(`Reset link sent to ${email}`, 'success');
    } catch (e) {
      addToast('Resend failed: ' + e.message, 'error');
    }
  }

  return (
    <div className="page-container">
      <div className="page-hdr">
        <h2>Team Management</h2>
        <p>Manage who has access to PP MAP v5. Only <strong>@kunlene-sz.com</strong> or <strong>@kunlene-yn.com</strong> emails can be invited. Invited users receive an email with a setup link — no manual password sharing needed.</p>
      </div>

      {/* Role explainer */}
      <div className="g3 mb-14">
        {Object.entries(ROLE_INFO).map(([key, info]) => (
          <div key={key} className={`card card-accent-${info.cardAccent}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span className={`badge ${info.badgeClass}`}>{info.label}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--t1)', lineHeight: 1.5 }}>{info.desc}</div>
          </div>
        ))}
      </div>

      {/* Invite form */}
      <div className="card mb-14">
        <div className="card-title"><span className="card-title-text">Invite new user</span></div>
        <form onSubmit={handleInvite}>
          <div className="g2 mb-12">
            <div className="form-group" style={{ margin: 0 }}>
              <div className="form-label"><span className="form-label-text">Work email</span></div>
              <input
                type="email"
                className="form-input"
                placeholder="colleague@kunlene-sz.com / @kunlene-yn.com"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                disabled={inviting}
                required
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <div className="form-label"><span className="form-label-text">Role</span></div>
              <select
                className="form-input"
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                disabled={inviting}
              >
                <option value="viewer">Viewer — read only</option>
                <option value="data_updater">Data Updater — edit & save</option>
                <option value="admin">Admin — full access</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={inviting}>
            {inviting ? 'Sending invitation...' : '✉️  Send invitation email'}
          </button>
          <div className="form-hint" style={{ marginTop: 8 }}>
            User will receive an email with a one-click link to set their password and sign in.
          </div>
        </form>
      </div>

      {/* Users list */}
      <div className="card">
        <div className="card-title"><span className="card-title-text">Team members ({users.length})</span></div>
        {loading ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--t2)', fontSize: 12 }}>Loading...</div>
        ) : users.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--t2)', fontSize: 12 }}>No users yet. Invite your first team member above.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invited by</th>
                  <th>Joined</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isMe = u.email === currentUser.email;
                  const linked = !!u.user_id;
                  const info = ROLE_INFO[u.role] || ROLE_INFO.viewer;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--t0)' }}>
                          {u.email}
                          {isMe && <span style={{ color: 'var(--t3)', marginLeft: 8, fontSize: 10 }}>(you)</span>}
                        </div>
                      </td>
                      <td>
                        {isMe ? (
                          <span className={`badge ${info.badgeClass}`}>{info.label}</span>
                        ) : (
                          <select
                            value={u.role}
                            onChange={e => changeRole(u.id, e.target.value)}
                            style={{
                              fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600,
                              padding: '3px 8px', borderRadius: 4,
                              border: '1px solid var(--border)',
                              background: 'var(--bg3)', color: 'var(--t0)',
                              cursor: 'pointer',
                            }}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="data_updater">Data Updater</option>
                            <option value="admin">Admin</option>
                          </select>
                        )}
                      </td>
                      <td>
                        {linked ? (
                          <span className="badge badge-grn">● Active</span>
                        ) : (
                          <span className="badge badge-amb">○ Pending</span>
                        )}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--t2)' }}>{u.invited_by || '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--t2)', fontFamily: 'var(--mono)' }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!isMe && (
                          <>
                            {!linked && (
                              <button
                                onClick={() => resendInvite(u.email)}
                                className="btn btn-sm"
                                style={{ marginRight: 6 }}
                                title="Resend invitation email"
                              >
                                ↻ Resend
                              </button>
                            )}
                            <button
                              onClick={() => removeUser(u.user_id, u.email)}
                              className="btn btn-sm btn-danger"
                              title="Remove access"
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="alert alert-grn" style={{ marginTop: 14 }}>
        <span className="alert-icon">✓</span>
        <div className="alert-body">
          <strong>How invitations work:</strong> When you click "Send invitation email", Supabase sends the user a secure email with a link to set their own password. They click → set password → automatically signed in. <strong>No manual password sharing required.</strong>
        </div>
      </div>
    </div>
  );
}
